import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { SessionService } from '@src/app/shared/services/session.service';
import { AttributeDefinitionDataType, AttributeDefinitionFormData } from './attribute-definition.model';

interface LanguageItem {
  language: string;
  code: string;
}

interface DataTypeOption {
  label: string;
  value: AttributeDefinitionDataType;
}

@Component({
  selector: 'app-attribute-definition-form',
  templateUrl: './attribute-definition-form.component.html',
  styleUrl: './attribute-definition-form.component.scss',
  standalone: false
})
export class AttributeDefinitionFormComponent implements OnInit {

  @Input() mode: 'create' | 'edit' = 'create';
  @Input() translationPrefix = 'RETAILER_ATTRIBUTES_DEFINITIONS_CREATE';
  @Input() entityName: 'product' | 'retailer' = 'retailer';
  @Input() formTestId = 'attribute-definition-form';
  @Input() definition?: AttributeDefinitionFormData;

  @Output() submitted = new EventEmitter<AttributeDefinitionFormData>();

  languages: LanguageItem[] = [];
  dataTypeOptions: DataTypeOption[] = [];

  showAttributeTranslations = false;

  attributeDefinitionForm: FormGroup = this.fb.group(
    {
      attributeCode: ['', [Validators.required, Validators.maxLength(100)]],
      dataType: [['FIXED_VALUES'], Validators.required],
      allowMultipleValues: [false, Validators.required],
      changesOverTime: [false, Validators.required],
      attributeTranslations: this.fb.group({}),
      valueCodes: this.fb.array([])
    },
    { validators: [this.requireValueCodeForFixedValuesType()] }
  );

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
    private session: SessionService
  ) { }

  async ngOnInit(): Promise<void> {
    // load translations before building labels
    await firstValueFrom(this.translate.get('PORTAL_LANGUAGES'));
    this.loadLanguages();

    this.buildDataTypeOptions();
    this.buildAttributeTranslationsGroup();
    this.handleDataTypeChanges();
    this.initialiseFormMode();
  }

  get valueCodes(): FormArray {
    return this.attributeDefinitionForm.get('valueCodes') as FormArray;
  }

  get attributeTranslationsGroup(): FormGroup {
    return this.attributeDefinitionForm.get('attributeTranslations') as FormGroup;
  }

  get isFixedValuesType(): boolean {
    return (
      this.getSelectedDataType() === 'FIXED_VALUES'
    );
  }

  private loadLanguages(): void {
    const companyLanguages = this.session.companySettings?.languages ?? [];

    const enabledLanguages = companyLanguages.filter(
      language => language.isEnabled
    );

    enabledLanguages.sort((a, b) => {
      const firstId = a.id ?? 0;
      const secondId = b.id ?? 0;

      return firstId - secondId;
    });

    this.languages = enabledLanguages.map(language => {
      const code = language.code || 'unknown';

      return {
        language: `PORTAL_LANGUAGES.${code.toUpperCase()}`,
        code
      };
    });
  }

  private buildDataTypeOptions(): void {
    this.dataTypeOptions = [
      {
        label: this.translate.instant(`${this.translationPrefix}.DATA_TYPE_FIXED_VALUES`),
        value: 'FIXED_VALUES'
      },
      {
        label: this.translate.instant(`${this.translationPrefix}.DATA_TYPE_DECIMAL`),
        value: 'DECIMAL'
      },
      {
        label: this.translate.instant(`${this.translationPrefix}.DATA_TYPE_INTEGER`),
        value: 'INTEGER'
      },
      {
        label: this.translate.instant(`${this.translationPrefix}.DATA_TYPE_TEXT`),
        value: 'TEXT'
      }
    ];
  }

  private initialiseFormMode(): void {
    // prepare controls for create & edit
    if (this.mode === 'create') {
      if (this.isFixedValuesType) {
        this.addValueCode();
      }

      return;
    }

    this.disableImmutableFields();

    if (this.definition) {
      this.patchForm(this.definition);
    }
  }

  private disableImmutableFields(): void {
    this.attributeDefinitionForm.get('dataType')?.disable();
    this.attributeDefinitionForm.get('allowMultipleValues')?.disable();
    this.attributeDefinitionForm.get('changesOverTime')?.disable();
  }

  private patchForm(definition: AttributeDefinitionFormData): void {
    this.attributeDefinitionForm.patchValue({
      attributeCode: definition.definitionCode,
      dataType: [definition.dataType],
      allowMultipleValues: definition.allowMultipleValues,
      changesOverTime: definition.changesOverTime
    });

    this.patchDefinitionTranslations(
      definition.translations
    );

    this.showAttributeTranslations = definition.translations.length > 0;

    this.patchDefinitionValues(definition.values);

    if (this.isFixedValuesType && this.valueCodes.length === 0) {
      this.addValueCode();
    }

    this.attributeDefinitionForm
      .updateValueAndValidity();
  }

  private patchDefinitionTranslations(translations: AttributeDefinitionFormData['translations']): void {
    // fill translated names
    for (const translation of translations) {
      this.attributeTranslationsGroup.get(translation.isoCode)?.setValue(translation.translatedName);
    }
  }

  private patchDefinitionValues(values: AttributeDefinitionFormData['values']): void {
    this.clearValueCodes();

    for (const value of values) {
      const valueGroup = this.createValueCodeGroup();

      valueGroup.patchValue({
        code: value.valueCode,
        codeIsActive: value.isActive,
        showTranslations: value.translations.length > 0,
        newValue: true
      });

      this.patchValueTranslations(valueGroup, value.translations);

      this.valueCodes.push(valueGroup);
    }
  }

  private patchValueTranslations(valueGroup: FormGroup, translations: AttributeDefinitionFormData['values'][number]['translations']): void {
    const translationsGroup = valueGroup.get('translations') as FormGroup;

    for (const translation of translations) {
      translationsGroup.get(translation.isoCode)?.setValue(translation.translatedValue);
    }
  }

  private buildAttributeTranslationsGroup(): void {
    const translationsGroup = this.createTranslationsGroup();
    this.attributeDefinitionForm.setControl('attributeTranslations', translationsGroup);
  }

  private createTranslationsGroup(): FormGroup {
    const group = this.fb.group({});

    for (const language of this.languages) {
      group.addControl(
        language.code,
        this.fb.control('')
      );
    }

    return group;
  }

  private createValueCodeGroup(): FormGroup {
    return this.fb.group({
      code: ['', [Validators.required, Validators.maxLength(100)]],
      codeIsActive: [true, Validators.required],
      showTranslations: [false],
      translations: this.createTranslationsGroup(),
      newValue: [false]
    });
  }

  private handleDataTypeChanges(): void {
    // keep value rows aligned with data type
    this.attributeDefinitionForm.get('dataType')?.valueChanges.subscribe(() => {
      if (!this.isFixedValuesType) {
        this.clearValueCodes();
        this.attributeDefinitionForm.updateValueAndValidity();
        return;
      }

      if (this.valueCodes.length === 0) {
        this.valueCodes.push(this.createValueCodeGroup());
      }

      this.attributeDefinitionForm.updateValueAndValidity();
    });
  }

  private clearValueCodes(): void {
    while (this.valueCodes.length > 0) {
      this.valueCodes.removeAt(0);
    }
  }

  private requireValueCodeForFixedValuesType(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const selectedType = control.get('dataType')?.value?.[0];

      if (selectedType !== 'FIXED_VALUES') {
        return null;
      }

      const valueCodes = control.get('valueCodes') as FormArray | null;

      // fixed values need at least one code
      if (!valueCodes || valueCodes.length === 0) {
        return {
          valueCodeRequired: true
        };
      }

      const hasValueCode = valueCodes.controls.some(valueControl => {
          const code = valueControl.get('code')?.value;
          const trimmedCode = String(code ?? '').trim();
          return trimmedCode.length > 0;
        });

      if (!hasValueCode) {
        return {
          valueCodeRequired: true
        };
      }

      return null;
    };
  }

  addValueCode(): void {
    const valueGroup = this.createValueCodeGroup();
    this.valueCodes.push(valueGroup);
    this.attributeDefinitionForm.updateValueAndValidity();
  }

  removeValueCode(index: number): void {
    this.valueCodes.removeAt(index);
    this.attributeDefinitionForm.updateValueAndValidity();
  }

  toggleAttributeTranslations(): void {
    this.showAttributeTranslations = !this.showAttributeTranslations;
  }

  toggleValueTranslations(index: number): void {
    const valueGroup = this.valueCodes.at(index) as FormGroup;
    const visibilityControl = valueGroup.get('showTranslations');
    const translationsAreVisible = visibilityControl?.value === true;

    visibilityControl?.setValue(!translationsAreVisible);
  }

  getValueTranslationsGroup(index: number): FormGroup {
    return this.valueCodes.at(index).get('translations') as FormGroup;
  }

  submitForm(): void {
    this.attributeDefinitionForm.markAllAsTouched();
    this.attributeDefinitionForm.updateValueAndValidity();

    if (this.attributeDefinitionForm.invalid) {
      return;
    }

    const dataType = this.getSelectedDataType();

    if (!dataType) {
      return;
    }

    const definition: AttributeDefinitionFormData = {
      definitionCode: this.getTrimmedAttributeCode(),
      dataType,
      allowMultipleValues: this.attributeDefinitionForm.get('allowMultipleValues')?.value === true,
      changesOverTime: this.attributeDefinitionForm.get('changesOverTime')?.value === true,
      translations: this.buildAttributeTranslations(),
      values:this.buildDefinitionValues()
    };

    this.submitted.emit(definition);
  }

  private buildAttributeTranslations(): AttributeDefinitionFormData['translations'] {
    const translations: AttributeDefinitionFormData['translations'] = [];

    for (const language of this.languages) {
      const translatedName = this.attributeTranslationsGroup.get(language.code)?.value;
      const trimmedName = String(translatedName ?? '').trim();

      if (!trimmedName) {
        continue;
      }

      translations.push({
        isoCode: language.code,
        translatedName: trimmedName
      });
    }

    return translations;
  }

  private buildDefinitionValues(): AttributeDefinitionFormData['values'] {
    const values: AttributeDefinitionFormData['values'] = [];

    for (const control of this.valueCodes.controls) {
      const valueGroup = control as FormGroup;
      const valueCode = String(valueGroup.get('code')?.value ?? '').trim();

      if (!valueCode) {
        continue;
      }

      values.push({
        valueCode,
        isActive: valueGroup.get('codeIsActive')?.value === true,
        translations: this.buildValueTranslations(valueGroup)
      });
    }

    return values;
  }

  private buildValueTranslations(valueGroup: FormGroup): AttributeDefinitionFormData['values'][number]['translations'] {
    const translations: AttributeDefinitionFormData['values'][number]['translations'] = [];

    const translationsGroup = valueGroup.get('translations') as FormGroup;

    for (const language of this.languages) {
      const translatedValue = translationsGroup.get(language.code)?.value;
      const trimmedValue = String(translatedValue ?? '').trim();

      if (!trimmedValue) {
        continue;
      }

      translations.push({
        isoCode: language.code,
        translatedValue: trimmedValue
      });
    }

    return translations;
  }

  private getSelectedDataType(): AttributeDefinitionDataType | undefined {
    // read the selected type from the dropdown
    return this.attributeDefinitionForm.get('dataType')?.value?.[0];
  }

  private getTrimmedAttributeCode(): string {
    const attributeCode = this.attributeDefinitionForm.get('attributeCode')?.value;

    return String(attributeCode ?? '').trim();
  }
}
